import { Box, Typography } from '@mui/material';
import { UserEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { UserAvatar } from 'shared/components/UserAvatar';
import { UserCardMenu } from 'shared/components/UserCardMenu';
import { useDefaultUserMenuActions } from 'shared/hooks';
import { CardAction } from 'shared/interfaces';
import { DefaultDisplayValue } from 'app/enums';
import { ValueOrDefaultFormat } from 'shared/components/Format/ValueOrDefaultFormat';
import { useStyles } from './PlayerTile.styles';

interface PlayerTileProps {
  user?: UserEntity;
  readonly?: boolean;
  actions?: CardAction<UserEntity>[];
  onClick?: (user: UserEntity) => void;
}

const PlayerTileComponent: FC<PlayerTileProps> = ({
  user,
  readonly = false,
  actions = [],
  onClick,
}) => {
  const { classes } = useStyles({ readonly });
  const { viewProfileAction } = useDefaultUserMenuActions();

  const isOneClickActionsAvailable = useMemo(() => !Boolean(onClick), [onClick]);

  const onClickHandler = useCallback(() => {
    if (!user) {
      return;
    }

    onClick?.(user as UserEntity);
  }, [onClick, user]);

  const preparedAction = useMemo(() => {
    return [...actions, viewProfileAction];
  }, [actions, viewProfileAction]);

  if (!user) {
    return null;
  }

  const tileLayout = (
    <Box onClick={onClickHandler} className={classNames(classes.root)}>
      <Box display="flex" alignItems="center">
        <Box className={classes.avatar}>
          <UserAvatar user={user} />
        </Box>
        <Typography pl={1} className={classes.userName}>
          <ValueOrDefaultFormat
            value={user?.userName}
            defaultValue={DefaultDisplayValue.NotAuthorized}
          />
        </Typography>
      </Box>
      {!readonly && (
        <Box className="actions">
          <UserCardMenu
            data={user}
            actions={preparedAction}
            readonly={isOneClickActionsAvailable}
          />
        </Box>
      )}
    </Box>
  );

  if (isOneClickActionsAvailable && !readonly) {
    return (
      <UserCardMenu data={user} actions={preparedAction}>
        {tileLayout}
      </UserCardMenu>
    );
  }

  return <>{tileLayout}</>;
};

export const PlayerTile = memo(PlayerTileComponent);
